import { useEffect, useRef } from 'react';
import type { Group, PhotoStyle } from '../lib/types';
import { GroupAvatar, MetaLine, SignUpButton, Tags, leaderNameOf } from './GroupCard';

interface GroupDetailProps {
  group: Group | null;
  onClose: () => void;
  showTerm?: boolean;
  photoStyle?: PhotoStyle;
}

/**
 * Detail panel for a single group.
 *
 * Uses the native <dialog> element, which brings the focus trap, Escape-to-close,
 * and inert background for free rather than hand-rolling them.
 */
export function GroupDetail({ group, onClose, showTerm, photoStyle = 'banner' }: GroupDetailProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (group && !dialog.open) dialog.showModal();
    if (!group && dialog.open) dialog.close();
  }, [group]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // Fires for Escape as well as dialog.close().
    const onCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('close', onClose);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('close', onClose);
    };
  }, [onClose]);

  // Backdrop click: the dialog element itself is the backdrop area around the panel.
  const onDialogClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === ref.current) onClose();
  };

  // Either part can be missing in CCB, so join rather than assuming both exist.
  const leaderName = group ? leaderNameOf(group) : '';

  return (
    <dialog ref={ref} className="detail" onClick={onDialogClick} aria-labelledby="detail-title">
      {group && (
        <div className="detail__panel">
          <div className="detail__header">
            <div className="detail__headings">
              <h2 className="detail__title" id="detail-title">
                {group.name}
              </h2>
            </div>
            <button type="button" className="detail__close" onClick={onClose} aria-label="Close">
              <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <div className="detail__body">
            {photoStyle === 'banner' && group.photo && (
              <div className="detail__media">
                <img src={group.photo} alt="" />
              </div>
            )}

            <MetaLine group={group} />
            <Tags group={group} showTerm={showTerm} />

            {/*
              Sanitized at build time down to an attribute-free tag set
              (p/br/strong/em/lists) — no hrefs, no styles, no event handlers.
            */}
            {group.description ? (
              <div
                className="detail__description"
                dangerouslySetInnerHTML={{ __html: group.description }}
              />
            ) : (
              <p className="detail__description detail__description--empty">
                No description has been added for this group yet.
              </p>
            )}

            <dl className="detail__facts">
              {showTerm && group.term && (
                <div>
                  <dt>Term</dt>
                  <dd>{group.term}</dd>
                </div>
              )}
              {/* House groups get both: where they meet, and which campus runs them. */}
              {group.meetsAtHome && (
                <div>
                  <dt>Meets at</dt>
                  <dd>{group.location}</dd>
                </div>
              )}
              <div>
                <dt>Campus</dt>
                <dd>{group.campus || '—'}</dd>
              </div>
              <div>
                <dt>Meets</dt>
                <dd>{[group.meetingDay, group.meetingTime].filter(Boolean).join(', ') || '—'}</dd>
              </div>
              {group.currentMembers !== null && group.currentMembers > 0 && (
                <div>
                  <dt>Members</dt>
                  <dd>
                    {group.currentMembers}
                    {group.capacity ? ` of ${group.capacity}` : ''}
                  </dd>
                </div>
              )}
              <div>
                <dt>Who it's for</dt>
                <dd>{group.audience || '—'}</dd>
              </div>
              <div>
                <dt>Kids Ministry</dt>
                <dd>{group.kidsMinistry ? 'Provided' : 'Not provided'}</dd>
              </div>
            </dl>

            {/* Same placement logic as the card: the leader is a byline under
                the facts, not a headline above them. Name only — never an
                email, phone, or CCB id. */}
            {leaderName && (
              <div className="detail__leader">
                {photoStyle === 'avatar' && <GroupAvatar group={group} size="lg" />}
                <span>Led by {leaderName}</span>
              </div>
            )}
          </div>

          <div className="detail__actions">
            <SignUpButton group={group} block />
          </div>
        </div>
      )}
    </dialog>
  );
}
